import { SceneChild } from '../types/portfolio_types'
import { useEffect, useState, useRef, useCallback } from 'react'
import { useAtom } from 'jotai'
import {
  currentInteractionAtom,
  playerLocationAtom,
  showHelpersAtom,
  boundingBoxesMapAtom,
} from '../store/store'
import * as THREE from 'three'
import { useFrame, useThree } from '@react-three/fiber'
import { determineYawToPointTo, getYawInDegrees, shouldSpinYawClockwise } from '../helpers/helperFunctions'
import useFaceManager from '../hooks/FaceManager'

type MailmanCatAnimationName = 'mailman_cat_idle' | 'mailman_cat_happy' | 'mailman_cat_walk'

const animation_speeds: { [key: string]: number } = {
  mailman_cat_walk: 1,
  mailman_cat_idle: 1,
  mailman_cat_happy: 1,
}

const MAILMAN_CAT_ID = 'mailman_cat_rig'
const MAILMAN_CAT_SENSOR = 'sensor_mailman_cat'
const BASE_YAW = 335

type MailmanCatProps = {
  mailman_cat_rig: SceneChild
  mixer: THREE.AnimationMixer
  sceneAnimations: THREE.AnimationClip[]
}

const MailmanCat = ({ mailman_cat_rig, mixer, sceneAnimations }: MailmanCatProps) => {
  const { scene } = useThree()

  const [currentAction, setCurrentAction] = useState<THREE.AnimationAction>()
  const [currentInteractionObj] = useAtom(currentInteractionAtom)
  const [playerLocationObj] = useAtom(playerLocationAtom)
  const [bbMap] = useAtom(boundingBoxesMapAtom)
  const [showHelpers] = useAtom(showHelpersAtom)

  const mailman_body = useRef<THREE.Object3D | null>(null)
  const target_yaw = useRef<number | null>(BASE_YAW)
  const spin_to_face_interact_clockwise = useRef(true)
  const mailman_cat_face_material_ref = useRef<THREE.MeshStandardMaterial | null>(null)

  const mailman_ref = useRef<THREE.Group | null>(null)

  useFaceManager({
    id: 'mailman_cat',
    face_name: 'standard cat',
    default_face: 'standard cat neutral',
    face_material: mailman_cat_face_material_ref.current,
    min_filter: THREE.NearestFilter,
  })

  const changeAction = useCallback(
    (animation_name: MailmanCatAnimationName) => {
      const current_clip = currentAction?.getClip()

      if (
        (!currentAction || (currentAction && current_clip && current_clip.name !== animation_name)) &&
        sceneAnimations &&
        mixer
      ) {
        const fadeDuration = 0.5
        currentAction?.fadeOut(fadeDuration)

        const animation_clip = sceneAnimations.find((clip) => clip.name === animation_name)!
        const animation = mixer.clipAction(animation_clip).reset().fadeIn(fadeDuration).play()
        animation.timeScale = animation_speeds[animation_name] || 1

        setCurrentAction(animation)
      }
    },
    [currentAction, mixer, sceneAnimations],
  )

  // 1) SET FRUSTUM CULLING + SET MATERIAL FILTER + START IDLE ANIMATION
  useEffect(() => {
    if (!mailman_cat_rig) return

    mailman_body.current = mailman_cat_rig.object

    mailman_cat_rig.object.traverse((child) => {
      if ((child as THREE.Mesh).isMesh) {
        child.castShadow = true
      }
    })

    const mailman_cat_face = mailman_cat_rig.object.children.find((obj) => obj.name === 'mailman_cat_face')!
    const mailman_cat_body = mailman_cat_rig.object.children.find((obj) => obj.name === 'mailman_cat_body')!
    const mailman_cat_shirt = mailman_cat_rig.object.children.find((obj) => obj.name === 'mailman_cat_shirt')!

    // Meshes are rendered only if they are visible in the camera (this is basically what frustum culling means)
    // However, suddenly rendering these meshes causes a frame drop, it's better to always render them from the beginning
    mailman_cat_face.frustumCulled = false
    mailman_cat_body.frustumCulled = false
    mailman_cat_shirt.frustumCulled = false

    const mailman_cat_face_material = (mailman_cat_face as THREE.Mesh).material as THREE.MeshStandardMaterial
    const mailman_cat_body_material = (mailman_cat_body as THREE.Mesh).material as THREE.MeshStandardMaterial

    if (mailman_cat_face_material.map && mailman_cat_body_material.map) {
      mailman_cat_face_material.polygonOffset = true
      mailman_cat_face_material.polygonOffsetFactor = -1

      mailman_cat_body_material.map.minFilter = THREE.LinearFilter
      mailman_cat_body_material.map.magFilter = THREE.LinearFilter

      mailman_cat_face_material_ref.current = mailman_cat_face_material
    }
  }, [currentAction, mailman_cat_rig])

  // 2) If the mailman cat is the currently inspecting object, set the current interaction location
  // so that Bianca can turn towards us, and then turn to her afterwards
  useEffect(() => {
    if (!mailman_body.current) return

    if (currentInteractionObj?.currentlyInspecting.id === MAILMAN_CAT_SENSOR && playerLocationObj) {
      target_yaw.current = determineYawToPointTo(
        playerLocationObj.world_position,
        mailman_body.current.position,
      )

      spin_to_face_interact_clockwise.current = shouldSpinYawClockwise(
        getYawInDegrees(mailman_body.current.quaternion),
        target_yaw.current,
      )

      return
    }

    const current_yaw = getYawInDegrees(mailman_body.current.quaternion)

    if (!currentInteractionObj && current_yaw !== BASE_YAW) {
      target_yaw.current = BASE_YAW

      spin_to_face_interact_clockwise.current = shouldSpinYawClockwise(
        getYawInDegrees(mailman_body.current.quaternion),
        target_yaw.current!,
      )
    }
  }, [currentInteractionObj, playerLocationObj])

  const targetYawSatisfied = () => {
    if (!mailman_body.current || target_yaw.current === null) return true

    const t_yaw = target_yaw.current
    const current_yaw = getYawInDegrees(mailman_body.current.quaternion)
    const offset = 4
    const target_yaw_low = t_yaw - offset < 0 ? t_yaw - offset + 360 : t_yaw - offset
    const target_yaw_high = t_yaw - offset > 359 ? t_yaw + offset - 360 : t_yaw + offset

    const wrap_around = target_yaw_low > target_yaw_high

    if (
      (!wrap_around && current_yaw > target_yaw_low && current_yaw < target_yaw_high) ||
      (wrap_around && (current_yaw >= target_yaw_low || current_yaw <= target_yaw_high))
    ) {
      target_yaw.current = null
      return true
    }

    return false
  }

  const updateBboxes = () => {
    if (!bbMap || !mailman_body.current) return

    bbMap[MAILMAN_CAT_ID].bbox.setFromObject(mailman_body.current)

    bbMap[MAILMAN_CAT_ID].bbox.min.x += 0.5
    bbMap[MAILMAN_CAT_ID].bbox.max.x -= 0.5
    bbMap[MAILMAN_CAT_ID].bbox.min.z += 0.9
    bbMap[MAILMAN_CAT_ID].bbox.max.z -= 0.2

    if (showHelpers) {
      const helper = scene.getObjectByName(`helper_${MAILMAN_CAT_ID}`) as THREE.Box3Helper
      helper.box.copy(bbMap[MAILMAN_CAT_ID].bbox)
    }
  }

  useFrame((_state, delta) => {
    if (!mailman_body.current || !bbMap || !bbMap[MAILMAN_CAT_ID]) return

    const safeDelta = Math.min(delta, 0.01)

    if (target_yaw.current && !targetYawSatisfied()) {
      changeAction('mailman_cat_walk')
      const spin_power = 3
      const spin_direction = spin_to_face_interact_clockwise.current ? -spin_power : spin_power
      const spin_y = spin_direction * safeDelta
      mailman_body.current.rotation.y += spin_y
      updateBboxes()
      return
    } else if (currentInteractionObj?.currentlyInspecting.id === MAILMAN_CAT_SENSOR) {
      return changeAction('mailman_cat_happy')
    }

    // This line sets the idle animation for the first time and then for all subsequent
    // times. Doing it in a useEffect throws a "No target node found for track" error
    // because the mesh hasn't been rendered into the scene yet.
    // It works in the useEffect if you pass the animation action as a prop
    // and play it there, something like setCurrentAction(idle_animation.play()), but
    // I'll just do it here this time.
    changeAction('mailman_cat_idle')
  })

  if (!mailman_cat_rig) return null

  return <group ref={mailman_ref}>{mailman_cat_rig.element}</group>
}

export default MailmanCat
